import { useEffect, useRef } from 'react';

import { MergeTestingCenterWizard } from 'pages/partners/components/MergeTestingCenterWizard/MergeTestingCenterWizard';
import { ETypeSector, Partner, PartnerApi, PartnerDetails, PartnerTypes } from 'apis/PartnerApi';
import GMetrixDialogConfirm from 'components/GMetrixDialogConfirm/GMetrixDialogConfirm';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import GMetrixDialogShell from 'components/GMetrixDialogShell/GMetrixDialogShell';
import { DEFAULT_PARTNER } from 'pages/partners/components/PartnerForm/constants';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { PartnerDetailsContext, usePartners } from 'pages/partners/context';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { Col, Form, Row } from 'react-bootstrap';
import { emailStringValidation } from 'utils';
import { useContext } from 'components/Tabs';
import { Country } from 'apis/CountryApi';
import { StateApi } from 'apis/StateApi';
import { useSuperState } from 'hooks';

import 'pages/partners/components/PartnerForm/styles/partner-form.scss';

type PartnerFormProps = {
    details: PartnerDetails;
    getPartnerTypeName: () => string;
    onDetailsItemClick: (dataItem: Partner, newPartner?: boolean) => Promise<void>;
}

export const PartnerForm = (props: PartnerFormProps) => {

    const { details, getPartnerTypeName, onDetailsItemClick } = props;

    const [state, setState] = useSuperState({
        currentOperatingCountryList: [] as Country[],
        formDetails: DEFAULT_PARTNER as PartnerDetails,
        homeCountryIsValid: true,
        languageIsValid: true,
        operatingCountries: [] as Country[],
        sectorIsValid: true,
        showEditModal: false,
        showMergeWizard: false,
        stateIsValid: true
    });
    const { formDetails, homeCountryIsValid, languageIsValid, sectorIsValid, showEditModal, showMergeWizard, stateIsValid } = state;

    const { formatMessage } = useIntl();
    const sectors = [
        { Id: ETypeSector.K12, Name: formatMessage({ id: 'k12_education.eccc589e', defaultMessage: 'K12 Education' }) },
        { Id: ETypeSector.Government, Name: formatMessage({ id: 'government.28df1366', defaultMessage: 'Government' }) },
        { Id: ETypeSector.Commercial, Name: formatMessage({ id: 'commercial.431dfb1b', defaultMessage: 'Commercial' }) },
        { Id: ETypeSector.HigherEducation, Name: formatMessage({ id: 'higher_education.64009dd3', defaultMessage: 'Higher Education' }) }
    ];
    const { pageContext, update } = useContext(PartnerDetailsContext);
    const { countries, languages, setStates, states } = usePartners();
    const { permissions } = usePartners();
    const { catchWithToast, createToast } = useNotification();

    const gmetrixConfirmRef = useRef<any>();
    const gmetrixDialogShellRef = useRef<any>();

    const validateNewPartnerDetails = () => {

        if (pageContext?.Data.partnerDetails) {

            const sectorValid = formDetails.Sector !== null;
            const languageValid = formDetails.Language.Id !== 0;
            const organizationValid = formDetails.Name.length > 0;
            const emailValid = formDetails.ContactEmail.length > 0 && emailStringValidation(formDetails.ContactEmail);
            const homeCountryValid = formDetails.Country.Id != 0;
            const stateValid = states.length > 1 ? formDetails.State.Id !== 0 : true;

            const errors: string[] = [];
            if (!organizationValid) errors.push(formatMessage({ id: 'organization_is_required.bc24b3d1', defaultMessage: 'Organization is required' }));
            if (!emailValid) {

                errors.push(formDetails.ContactEmail.length === 0
                    ? formatMessage({ id: 'corporate_email_is_required.3452cf0b', defaultMessage: 'Corporate email is required' })
                    : formatMessage({ id: 'please_enter_a_valid_email_address.9e89059d', defaultMessage: 'Please enter a valid email address' }));

            }
            if (!sectorValid) errors.push(formatMessage({ id: 'sector_is_required.461eda94', defaultMessage: 'Sector is required' }));
            if (!languageValid) errors.push(formatMessage({ id: 'language_is_required.e4e4fde8', defaultMessage: 'Language is required' }));
            if (!homeCountryValid) errors.push(formatMessage({ id: 'home_country_is_required.ec5d23be', defaultMessage: 'Home Country is required' }));
            if (!stateValid) errors.push(formatMessage({ id: 'stateprovince_is_required.107b9aa7', defaultMessage: 'State/Province is required' }));

            setState({ homeCountryIsValid: homeCountryValid, languageIsValid: languageValid, sectorIsValid: sectorValid, stateIsValid: stateValid });

            if (errors.length > 0) {

                createToast(errors.join('. ') + '.', 'danger');
                return false;

            }
            return true;

        }

    };

    const handleEditPartnerClick = async () => {

        if (validateNewPartnerDetails()) {

            try {

                await PartnerApi.Update(formDetails);
                onDetailsItemClick(formDetails, false);
                createToast(formatMessage({ id: 'data_was_saved_successfully.c12a4ebc', defaultMessage: 'Data was saved successfully' }), 'success');
                setState({ showEditModal: false });

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_update_partner.499baa0c', defaultMessage: 'Failed to update partner.' }))(e);

            }

        }

    };

    const filterOutActiveCountries = (operatingCountries: Country[]) => countries.filter(country => {

        if (operatingCountries && operatingCountries.length > 0) {

            const hasOperatingCountry = operatingCountries.find(opCountry => opCountry.Id == country.Id);

            if (!hasOperatingCountry) {

                return country;

            }

        } else {

            return country;

        }

    }).filter(country => country.Id !== details.Country.Id);

    const PartnerForm = () => (
        <Form key={'partnerForm'} >
            <Row className="mb-2">
                <Form.Group as={Col} className="mt-2" controlId="formStudentFName" md={4}>
                    <GMetrixInput required label={formatMessage({ id: 'organization.ca6cc51d', defaultMessage: 'Organization' })} onChange={e => setState({ formDetails: { ...formDetails, Name: e.target.value } })} type="text" value={formDetails?.Name} />
                </Form.Group>
                <Form.Group as={Col} className="mt-2" controlId="formStudentLName" md={4}>
                    <GMetrixInput required label={formatMessage({ id: 'corporate_email.b483f416', defaultMessage: 'Corporate Email' })} onChange={e => setState({ formDetails: { ...formDetails, ContactEmail: e.target.value } })} type="text" value={formDetails?.ContactEmail} />
                </Form.Group>
            </Row>
            <Row className="mb-2">
                <Form.Group as={Col} className="mt-2" controlId="formStudentEmail" md={4}>
                    <GMetrixDropdown
                        required
                        value={{
                            Id: formDetails.Sector,
                            Name: sectors.find(sector => sector.Id === formDetails.Sector)
                                ?.Name
                        }}
                        dataItemKey="Id"
                        dataItems={sectors}
                        isValid={sectorIsValid}
                        label={formatMessage({ id: 'sector.99a79448', defaultMessage: 'Sector' })}
                        onChange={e => setState({ formDetails: { ...formDetails, Sector: e.Id } })}
                        textField="Name"
                    />
                </Form.Group>
                <Form.Group as={Col} className="mt-2" controlId="formStudentFName" md={4}>
                    <GMetrixDropdown
                        required
                        dataItemKey="Id"
                        dataItems={languages}
                        isValid={languageIsValid}
                        label={formatMessage({ id: 'language.bc205d83', defaultMessage: 'Language' })}
                        onChange={e => setState({ formDetails: { ...formDetails, Language: e } })}
                        textField="Name"
                        value={formDetails?.Language}
                    />
                </Form.Group>
            </Row>
            <Row className="mb-2">
                <Form.Group as={Col} className="mt-2" controlId="formStudentFName" md={4}>
                    <GMetrixDropdown
                        required
                        onChange={async value => {

                            const e = value as Country;
                            setState({ formDetails: { ...formDetails, Country: e, State: { Id: 0, Name: '' } } });
                            setState({ homeCountryIsValid: e.Id !== 0 });
                            try {

                                const fetchedStates = await StateApi.GetAll(e.Id);
                                setStates(fetchedStates);
                                if (fetchedStates.length === 0) {

                                    setState({ formDetails: { ...state.formDetails, State: { Id: 219, Name: formatMessage({ id: 'not_applicable.a01fc143', defaultMessage: 'Not Applicable' }) } } });

                                }

                            } catch (err) {

                                catchWithToast(formatMessage({ id: 'failed_to_load_states.ec2a3c83', defaultMessage: 'Failed to load states.' }))(err);

                            }

                        }}
                        dataItemKey="Id"
                        dataItems={countries}
                        isValid={homeCountryIsValid}
                        label={formatMessage({ id: 'country.cfeb54cc', defaultMessage: 'Country' })}
                        textField="Name"
                        value={formDetails?.Country}
                    />
                </Form.Group>
                <Form.Group as={Col} className="mt-2" controlId="formStudentFName" md={4}>
                    <GMetrixDropdown
                        dataItemKey="Id"
                        dataItems={states}
                        disabled={states.length === 1 || formDetails?.State?.Id === 219}
                        isValid={stateIsValid}
                        label={formatMessage({ id: 'stateprovince.d2dfa3c8', defaultMessage: 'State/Province' })}
                        onChange={e => setState({ formDetails: { ...formDetails, State: e } })}
                        required={states.length > 1 && formDetails?.State?.Id !== 219}
                        textField="Name"
                        value={formDetails?.State}
                    />
                </Form.Group>
            </Row>
            <Row className="mb-2">
                <Form.Group as={Col} className="mt-2" controlId="formStudentFName" md={4}>
                    <GMetrixInput label={formatMessage({ id: 'address_line_1.25dd7c0d', defaultMessage: 'Address Line 1' })} onChange={e => setState({ formDetails: { ...formDetails, Address1: e.target.value } })} type="text" value={formDetails?.Address1} />
                </Form.Group>
                <Form.Group as={Col} className="mt-2" controlId="formStudentFName" md={4}>
                    <GMetrixInput label={formatMessage({ id: 'address_line_2.74e16278', defaultMessage: 'Address Line 2' })} onChange={e => setState({ formDetails: { ...formDetails, Address2: e.target.value } })} type="text" value={formDetails?.Address2} />
                </Form.Group>
            </Row>
            <Row className="mb-2">
                <Form.Group as={Col} className="mt-2" controlId="formStudentFName" md={4}>
                    <GMetrixInput label={formatMessage({ id: 'city.e7690187', defaultMessage: 'City' })} onChange={e => setState({ formDetails: { ...formDetails, City: e.target.value } })} type="text" value={formDetails?.City} />
                </Form.Group>
                <Form.Group as={Col} className="mt-2" controlId="formStudentLName" md={4}>
                    <GMetrixInput label={formatMessage({ id: 'zippostal_code.9631e317', defaultMessage: 'Zip/Postal Code' })} onChange={e => setState({ formDetails: { ...formDetails, Zip: e.target.value } })} type="text" value={formDetails?.Zip} />
                </Form.Group>
            </Row>
            <Row className="mb-2">
                <Form.Group as={Col} className="mt-2" controlId="formStudentFName" md={4}>
                    <GMetrixInput label={formatMessage({ id: 'customer_id.29f2845e', defaultMessage: 'Customer ID' })} onChange={e => setState({ formDetails: { ...formDetails, CustomerIds: e.target.value } })} type="text" value={formDetails?.CustomerIds} />
                </Form.Group>
                <Form.Group as={Col} className="mt-2" controlId="formStudentLName" md={4}>
                    <GMetrixInput label={formatMessage({ id: 'phone.717d125a', defaultMessage: 'Phone' })} onChange={e => setState({ formDetails: { ...formDetails, Phone: e.target.value } })} type="text" value={formDetails?.Phone} />
                </Form.Group>
            </Row>
            <Row className="mb-2">
                <Form.Group as={Col} className="mt-2" controlId="formStudentFName" md={4}>
                    <GMetrixCheckInput
                        checked={formDetails?.Active}
                        label={formatMessage({ id: 'active.b9616c88', defaultMessage: 'Active' })}
                        onChange={e => setState({ formDetails: { ...formDetails, Active: e.target.checked } })}
                        type="switch"
                    />
                </Form.Group>

            </Row>
        </Form>
    );

    const verifyInput = () => {

        let errorFound = false;

        if (formDetails.Name.length === 0) {

            errorFound = true;

        }
        if (formDetails.ContactEmail.length === 0) {

            errorFound = true;

        }
        if (formDetails.Country.Name.length === 0) {

            errorFound = true;

        }

        return errorFound;

    };

    useEffect(() => {

        if (details.PartnerType !== PartnerTypes.TestingCenter) {

            const loadCountries = async () => {

                try {

                    const operatingCountries = await PartnerApi.GetPartnerOperatingCountries(details.ID);
                    if (pageContext?.Data.partnerDetails && pageContext.Data.originalPartnerDetails) {

                        setState({ currentOperatingCountryList: operatingCountries, operatingCountries: filterOutActiveCountries(operatingCountries) });
                        update({
                            originalPartnerDetails: {
                                ...pageContext.Data.originalPartnerDetails,
                                OperatingCountries: operatingCountries
                            },
                            partnerDetails: {
                                ...pageContext.Data.partnerDetails,
                                OperatingCountries: operatingCountries

                            }
                        });

                    }

                } catch (e) {

                    catchWithToast(formatMessage({ id: 'failed_to_load_operating_countries.4a471edd', defaultMessage: 'Failed to load operating countries.' }))(e);

                }

            };
            loadCountries();

        }

    }, []);

    useEffect(() => {

        const partnerDetails = pageContext?.Data?.partnerDetails;
        if (partnerDetails) {

            setState({ formDetails: partnerDetails });

            if (partnerDetails.Country?.Id) {

                const loadStates = async () => {

                    try {

                        const fetchedStates = await StateApi.GetAll(partnerDetails.Country.Id);
                        setStates(fetchedStates);
                        if (fetchedStates.length === 0) {

                            setState({ formDetails: { ...state.formDetails, State: { Id: 219, Name: formatMessage({ id: 'not_applicable.a01fc143', defaultMessage: 'Not Applicable' }) } } });

                        }

                    } catch (e) {

                        catchWithToast(formatMessage({ id: 'failed_to_load_states.ec2a3c83', defaultMessage: 'Failed to load states.' }))(e);

                    }

                };
                loadStates();

            }

        }

    }, [pageContext?.Data?.partnerDetails]);

    return (
        <>
            {pageContext?.Data.partnerDetails && (
                <>

                    <Row className="gap-3 gap-md-0">

                        <div className="d-flex justify-content-md-end flex-wrap gap-2">

                            {details.PartnerType === PartnerTypes.TestingCenter && permissions.MergePartner && (
                                <GMetrixButton
                                    className="float-right"
                                    iconClass="fa-code-merge fa-solid"
                                    onClick={() => setState({ showMergeWizard: true })}
                                    variant="outline-danger"
                                >
                                    {<FormattedMessage defaultMessage="Merge with TC" id="merge_with_tc.bfd71149" /> || 'Merge with TC'}
                                </GMetrixButton>
                            )}

                            <GMetrixButton
                                className="float-right"
                                iconClass="fa-pencil fa-solid"
                                onClick={() => setState({ showEditModal: true })}
                                variant="primary"
                            >
                                <FormattedMessage defaultMessage="Edit" id="edit.00978daf" />
                            </GMetrixButton>

                        </div>

                        <Col md={8}>

                            <Row as="dl" className="g-2 g-md-3 mb-0">

                                <Col md={6} xs={12}>
                                    <dt>
                                        <FormattedMessage defaultMessage="Partner Type" id="partner_type.583106ab" />
                                    </dt>
                                    <dd className="mb-0">
                                        {getPartnerTypeName()}
                                    </dd>
                                </Col>
                                <Col md={6} xs={12}>
                                    <dt>
                                        <FormattedMessage defaultMessage="Active" id="active.b9616c88" />
                                    </dt>
                                    <dd className="text-muted mb-0">
                                        {pageContext?.Data?.partnerDetails?.Active ? <i className="fa-solid fa-check text-success fa-lg"></i> : <i className="fa-solid fa-xmark text-danger fa-lg"></i>}
                                    </dd>
                                </Col>

                                <Col md={6} xs={12}>
                                    <dt>
                                        <FormattedMessage defaultMessage="Organization" id="organization.ca6cc51d" />
                                    </dt>
                                    <dd className="text-wrap mb-0">
                                        {pageContext?.Data?.partnerDetails?.Name ?? 'N/A'}
                                    </dd>
                                </Col>
                                <Col md={6} xs={12}>
                                    <dt>
                                        <FormattedMessage defaultMessage="Sector" id="sector.99a79448" />
                                    </dt>
                                    <dd className="text-muted mb-0">
                                        {pageContext?.Data?.partnerDetails?.SectorName ?? 'N/A'}
                                    </dd>
                                </Col>

                                <Col md={6} xs={12}>
                                    <dt>
                                        <FormattedMessage defaultMessage="Customer ID" id="customer_id.29f2845e" />
                                    </dt>
                                    <dd className="text-muted mb-0">
                                        {pageContext?.Data?.partnerDetails?.CustomerIds ?? 'N/A'}
                                    </dd>
                                </Col>
                                <Col md={6} xs={12}>
                                    <dt>
                                        <FormattedMessage defaultMessage="Home Country" id="home_country.829daf61" />
                                    </dt>
                                    <dd className="text-muted mb-0">
                                        {pageContext?.Data?.partnerDetails?.Country.Name ?? 'N/A'}
                                    </dd>
                                </Col>

                                <Col md={6} xs={12}>
                                    <dt>
                                        <FormattedMessage defaultMessage="Language" id="language.bc205d83" />
                                    </dt>
                                    <dd className="text-muted mb-0">
                                        {pageContext?.Data?.partnerDetails?.Language.Name ?? 'N/A'}
                                    </dd>
                                </Col>
                                <Col md={6} xs={12}>
                                    <dt>
                                        <FormattedMessage defaultMessage="Phone" id="phone.717d125a" />
                                    </dt>
                                    <dd className="text-muted mb-0">
                                        {pageContext?.Data?.partnerDetails?.Phone ?? 'N/A'}
                                    </dd>
                                </Col>

                                <Col md={6} xs={12}>
                                    <dt>
                                        <FormattedMessage defaultMessage="Address Line 1" id="address_line_1.25dd7c0d" />
                                    </dt>
                                    <dd className="text-muted mb-0">
                                        {pageContext?.Data?.partnerDetails?.Address1 ?? 'N/A'}
                                    </dd>
                                </Col>
                                <Col md={6} xs={12}>
                                    <dt>
                                        <FormattedMessage defaultMessage="Address Line 2" id="address_line_2.74e16278" />
                                    </dt>
                                    <dd className="text-muted mb-0">
                                        {pageContext?.Data?.partnerDetails?.Address2 ?? 'N/A'}
                                    </dd>
                                </Col>

                                <Col md={6} xs={12}>
                                    <dt>
                                        <FormattedMessage defaultMessage="City" id="city.e7690187" />
                                    </dt>
                                    <dd className="text-muted mb-0">
                                        {pageContext?.Data?.partnerDetails?.City ?? 'N/A'}
                                    </dd>
                                </Col>
                                <Col md={6} xs={12}>
                                    <dt>
                                        <FormattedMessage defaultMessage="State/Province" id="stateprovince.d2dfa3c8" />
                                    </dt>
                                    <dd className="text-muted mb-0">
                                        {pageContext?.Data?.partnerDetails?.State.Name ?? 'N/A'}
                                    </dd>
                                </Col>

                                <Col md={6} xs={12}>
                                    <dt>
                                        <FormattedMessage defaultMessage="Zip/Postal Code" id="zippostal_code.9631e317" />
                                    </dt>
                                    <dd className="text-muted mb-0">
                                        {pageContext?.Data?.partnerDetails?.Zip ?? 'N/A'}
                                    </dd>
                                </Col>
                            </Row>
                        </Col >

                    </Row >

                    <GMetrixDialogConfirm ref={gmetrixConfirmRef} />

                    <GMetrixDialogShell ref={gmetrixDialogShellRef} />

                    <GMetrixDialog
                        onCancel={() => {

                            const details = pageContext?.Data?.partnerDetails;
                            if (details) {

                                setState({ formDetails: details });

                            }
                            setState({ showEditModal: false });

                        }}
                        actionButtonClick={handleEditPartnerClick}
                        actionButtonDisabled={verifyInput()}
                        actionButtonText={formatMessage({ id: 'save.d44082ae', defaultMessage: 'Save' })}
                        dialogLabel={formatMessage({ id: 'edit_details.429ca491', defaultMessage: 'Edit Details' })}
                        show={showEditModal}
                        size="lg"
                    >
                        {PartnerForm()}
                    </GMetrixDialog>

                    <MergeTestingCenterWizard
                        onMergeComplete={async () => {

                            setState({ showMergeWizard: false });
                            await onDetailsItemClick(details, false);

                        }}
                        isOpen={showMergeWizard}
                        onClose={() => setState({ showMergeWizard: false })}
                        sourcePartner={details}
                    />
                </>
            )}
        </>
    );

};
